/* The feature descriptor every `web/src/features/<id>/index.ts` default-exports (BUILD-CONTRACT
   "Web feature pattern"). The shell builds the rail, bottom bar, Ctrl+K palette and routes from it.

   export default defineFeature({
     id: 'mail', title: 'Mail & calendar', icon: Mail, group: 'Apps', route: '/mail',
     screen: lazy(() => import('./Screen.tsx')),
     capabilities: ['OFFICE-05'],
   });
*/
import type { ComponentType, LazyExoticComponent } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { NavigateFunction } from 'react-router';

export type FeatureGroup = 'Work' | 'Apps' | 'You' | 'Account';
export const FEATURE_GROUPS: FeatureGroup[] = ['Work', 'Apps', 'You', 'Account'];

export interface CommandContext {
  navigate: NavigateFunction;
  toast: (message: string) => void;
}

export interface FeatureCommand {
  id: string;
  title: string;
  /** Route to open (absolute, e.g. '/chat/new'), or a function to run. */
  to?: string;
  run?: (ctx: CommandContext) => void;
  keywords?: string[];
  /** Display-only shortcut hint, e.g. 'Ctrl+N'. */
  shortcut?: string;
}

export interface FeatureDescriptor {
  /** Folder name; unique. */
  id: string;
  title: string;
  /** Short label for the rail and bottom bar (defaults to title). */
  short?: string;
  icon: LucideIcon | ComponentType<{ size?: number; 'aria-hidden'?: boolean | 'true' }>;
  group: FeatureGroup;
  /** Mount path, e.g. '/chat'. The screen also receives every sub-path ('/chat/*'). */
  route: string;
  screen: LazyExoticComponent<ComponentType>;
  commands?: FeatureCommand[];
  /** Capability IDs this feature delivers (each must also appear as data-cap in the screen). */
  capabilities: string[];
  /** Sort order inside its group (lower first; default 100). */
  order?: number;
  /** Show on the open-fold rail (at most 8 across features; extras stay in the sidebar). */
  rail?: boolean;
  /** Show on the cover-screen bottom bar (3 destinations plus More). */
  bottom?: boolean;
  /** Registered (its route redirects, its capabilities still count) but not listed in the sidebar, rail, bottom bar or palette. Used when a screen was merged into another (Ask). */
  hidden?: boolean;
  /** Reachable without signing in (sign-up, sign-in, legal pages). */
  public?: boolean;
  /** Full-bleed screen with no shell navigation (onboarding). */
  bare?: boolean;
  /** Badge count on the nav item (running agents, approvals). A plain getter is re-read every 2 s. */
  badge?: () => number | undefined;
  /** Reactive badge: a hook called while the nav item renders (e.g. `() => useApprovalCount((s) => s.count) || undefined`). */
  useBadge?: () => number | undefined;
  /** Public pages outside the shell (signed out, minimal layout), e.g. `[{ path: '/f/:slug', title: 'Form', screen }]`. */
  publicRoutes?: PublicRoute[];
}

export interface PublicRoute {
  /** Absolute path pattern, e.g. '/f/:slug'. */
  path: string;
  title: string;
  screen: LazyExoticComponent<ComponentType>;
}

/** Identity helper that type-checks a descriptor. */
export function defineFeature(f: FeatureDescriptor): FeatureDescriptor {
  return f;
}
